import { PageTypes } from 'utils/chat/instructorHugoContext';

const PAGE_CONFIG = {
    [PageTypes.COURSE_LIST]: 'Courses',
    [PageTypes.ASSIGNMENT_LIST]: 'Assignments',
    [PageTypes.ASSIGNMENT_CREATE]: 'Create Assignment',
    [PageTypes.ASSIGNMENT_OVERVIEW]: 'Assignment Overview',
    [PageTypes.ASSIGNMENT_EDIT]: 'Assignment Edit',
    [PageTypes.GRADING]: 'Grading',
    [PageTypes.ASSIGNMENT_PREVIEW]: 'Assignment Preview',
    [PageTypes.SUBMISSION_LIST]: 'Submissions',
    [PageTypes.SUBMISSION_DETAIL]: 'Submission Detail',
    [PageTypes.PEOPLE_LIST]: 'People',
    [PageTypes.RUBRIC_LIST]: 'Rubric Templates',
    [PageTypes.RUBRIC_CREATE]: 'Create Rubric',
    [PageTypes.RUBRIC_OVERVIEW]: 'Rubric Overview',
    [PageTypes.RUBRIC_EDIT]: 'Rubric Edit',
    [PageTypes.UNKNOWN]: 'General',
};

const PAGE_SUGGESTIONS = {
    [PageTypes.COURSE_LIST]: [
        { key: 'chat.hugo.suggestions.dashboard-progress', default: 'What should I check first in this course?' },
        { key: 'chat.hugo.suggestions.dashboard-next', default: 'Help me find assignments that need attention.' },
        { key: 'chat.hugo.suggestions.dashboard-summary', default: 'Summarize what is happening in this course.' },
    ],
    [PageTypes.ASSIGNMENT_LIST]: [
        { key: 'chat.hugo.suggestions.assignments-review', default: 'Which assignments should I review first?' },
        { key: 'chat.hugo.suggestions.assignments-improve', default: 'Help me improve an assignment setup.' },
        { key: 'chat.hugo.suggestions.assignments-next', default: 'What should I do before publishing an assignment?' },
    ],
    [PageTypes.ASSIGNMENT_CREATE]: [
        { key: 'chat.hugo.suggestions.create-outline', default: 'Help me outline this assignment.' },
        { key: 'chat.hugo.suggestions.create-rubric', default: 'Suggest rubric criteria for this assignment.' },
        { key: 'chat.hugo.suggestions.create-instructions', default: 'Review my assignment instructions for clarity.' },
    ],
    [PageTypes.ASSIGNMENT_OVERVIEW]: [
        { key: 'chat.hugo.suggestions.overview-review', default: 'What can you help me with here?' },
        { key: 'chat.hugo.suggestions.overview-status', default: 'Help me review this assignment status.' },
        { key: 'chat.hugo.suggestions.overview-next', default: 'What should I check next for this assignment?' },
    ],
    [PageTypes.ASSIGNMENT_EDIT]: [
        { key: 'chat.hugo.suggestions.setup-review', default: 'Review this assignment setup.' },
        { key: 'chat.hugo.suggestions.setup-rubric', default: 'Help me align the rubric with the instructions.' },
        { key: 'chat.hugo.suggestions.setup-feedback', default: 'Suggest feedback settings for this assignment.' },
    ],
    [PageTypes.GRADING]: [
        { key: 'chat.hugo.suggestions.grading-consistency', default: 'Help me grade this consistently.' },
        { key: 'chat.hugo.suggestions.grading-rubric', default: 'Explain how to apply this rubric.' },
        { key: 'chat.hugo.suggestions.grading-feedback', default: 'Help me write clearer feedback.' },
    ],
    [PageTypes.ASSIGNMENT_PREVIEW]: [
        { key: 'chat.hugo.suggestions.preview-check', default: 'What should I check in this preview?' },
        { key: 'chat.hugo.suggestions.preview-student', default: 'Does this look clear from a student perspective?' },
        { key: 'chat.hugo.suggestions.preview-improve', default: 'Suggest improvements before I publish.' },
    ],
    [PageTypes.SUBMISSION_LIST]: [
        { key: 'chat.hugo.suggestions.submissions-prioritize', default: 'Help me prioritize these submissions.' },
        { key: 'chat.hugo.suggestions.submissions-progress', default: 'Summarize submission progress.' },
        {
            key: 'chat.hugo.suggestions.submissions-patterns',
            default: 'What patterns should I look for while reviewing?',
        },
    ],
    [PageTypes.SUBMISSION_DETAIL]: [
        { key: 'chat.hugo.suggestions.submission-detail-review', default: 'Help me review this submission.' },
        { key: 'chat.hugo.suggestions.submission-detail-grade', default: 'Explain this grade.' },
        { key: 'chat.hugo.suggestions.submission-detail-feedback', default: 'Help me improve this feedback.' },
    ],
    [PageTypes.PEOPLE_LIST]: [
        { key: 'chat.hugo.suggestions.people-progress', default: 'Help me understand student progress.' },
        { key: 'chat.hugo.suggestions.people-support', default: 'Which students may need support?' },
        { key: 'chat.hugo.suggestions.people-roster', default: 'Help me review this course roster.' },
    ],
    [PageTypes.RUBRIC_LIST]: [
        { key: 'chat.hugo.suggestions.rubrics-find', default: 'Help me find a rubric to reuse.' },
        { key: 'chat.hugo.suggestions.rubrics-organize', default: 'Which rubric templates need attention?' },
        { key: 'chat.hugo.suggestions.rubrics-next', default: 'What should I check before creating a new rubric?' },
    ],
    [PageTypes.RUBRIC_CREATE]: [
        { key: 'chat.hugo.suggestions.rubric-create-criteria', default: 'Suggest criteria for this rubric.' },
        { key: 'chat.hugo.suggestions.rubric-create-levels', default: 'Help me define performance levels.' },
        { key: 'chat.hugo.suggestions.rubric-create-review', default: 'Review this rubric setup for clarity.' },
    ],
    [PageTypes.RUBRIC_OVERVIEW]: [
        { key: 'chat.hugo.suggestions.rubric-overview-review', default: 'Review this rubric for clarity.' },
        { key: 'chat.hugo.suggestions.rubric-overview-criteria', default: 'Suggest stronger rubric criteria.' },
        {
            key: 'chat.hugo.suggestions.rubric-overview-levels',
            default: 'Help me make performance levels more specific.',
        },
    ],
    [PageTypes.RUBRIC_EDIT]: [
        { key: 'chat.hugo.suggestions.rubric-edit-review', default: 'Review these rubric edits.' },
        { key: 'chat.hugo.suggestions.rubric-edit-criteria', default: 'Help me improve these criteria.' },
        { key: 'chat.hugo.suggestions.rubric-edit-levels', default: 'Help me tighten the performance levels.' },
    ],
    [PageTypes.UNKNOWN]: [
        { key: 'chat.hugo.suggestions.general-help', default: 'What can you help me with here?' },
        { key: 'chat.hugo.suggestions.general-next', default: 'Suggest a next step for this page.' },
        { key: 'chat.hugo.suggestions.general-explain', default: 'Explain what I can do on this page.' },
    ],
};

export const getPageLabel = (pageType) => PAGE_CONFIG[pageType] || PAGE_CONFIG[PageTypes.UNKNOWN];

export const getPageSuggestions = (pageType, t) => {
    const suggestions = PAGE_SUGGESTIONS[pageType] || PAGE_SUGGESTIONS[PageTypes.UNKNOWN];
    return suggestions.map((suggestion) => ({
        text: t(suggestion.key, suggestion.default),
    }));
};
